/**
 * @component Modal
 * @description Wiederverwendbarer Dialog/Modal mit Overlay, Escape-Handling
 * und konfigurierbarer Groesse. Sperrt Body-Scroll bei Oeffnung.
 *
 * @prop {boolean}   open     - Ob der Modal sichtbar ist
 * @prop {function}  onClose  - Callback zum Schliessen
 * @prop {string}    [title]  - Titel in der Kopfzeile
 * @prop {ReactNode} children - Inhalt des Modals
 * @prop {ReactNode} [footer] - Optionaler Footer-Bereich
 * @prop {string}    [size='md'] - Groesse (sm, md, lg, xl, full)
 * @usage Verwendet fuer Formulare, Bestaetigungen und Detailansichten
 */
import { useEffect } from 'react';

const SIZES = {
  sm:   'max-w-[400px]',
  md:   'max-w-[560px]',
  lg:   'max-w-[720px]',
  xl:   'max-w-[960px]',
  full: 'max-w-[calc(100vw-4rem)]',
};

export function Modal({ open, onClose, title, children, footer, size = 'md' }) {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className={`relative bg-surface-elevated border border-line-hover rounded-card w-full ${SIZES[size] || SIZES.md} max-h-[85vh] flex flex-col shadow-modal animate-slide-in`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-line shrink-0">
          <h2 className="text-base font-semibold text-content">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-[var(--text-tertiary)] hover:text-content hover:bg-surface-hover transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-line shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
